import { createRouter, createWebHistory } from "vue-router";
import { store } from "@/stores";

import { authenticationGuard } from "@/router/authentication";
import { installChunkRecovery } from "@/router/chunkRecovery";

const ActionItemsView = () => import("@/pages/ActionItemsView.vue");
const AIUsageView = () => import("@/pages/AIUsage.vue");
const AllCandidatesRedirectView = () =>
  import("@/pages/AllCandidatesRedirect.vue");
const AtsPipelineSyncView = () => import("@/pages/AtsPipelineSyncView.vue");
const AutoInterviewInviteView = () =>
  import("@/pages/AutoInterviewInviteView.vue");
const AutoRejectionMessageView = () =>
  import("@/pages/AutoRejectionMessage.vue");
const AutomationView = () => import("@/pages/AutomationView.vue");
const FaqLibraryView = () => import("@/pages/FaqLibraryView.vue");
const InterviewReportOpeningTextView = () =>
  import("@/pages/InterviewReportOpeningText.vue");
const CandidateEditView = () => import("@/pages/CandidateEdit.vue");
const CandidateListView = () => import("@/pages/CandidateList.vue");
const DashboardView = () => import("@/pages/Dashboard.vue");
const RejectView = () => import("@/pages/RejectView.vue");
const EvaluationView = () => import("@/pages/Evaluation.vue");
const EvaluationTranscriptRecordView = () =>
  import("@/pages/EvaluationTranscriptRecordView.vue");
const GlobalCandidatesListView = () =>
  import("@/pages/GlobalCandidatesList.vue");
const HomeView = () => import("@/pages/Home.vue");
const RoiMetricsView = () => import("@/pages/RoiMetrics.vue");
const IntegrationAddView = () => import("@/pages/IntegrationAdd.vue");
const LoginView = () => import("@/pages/Login.vue");
const LogoutView = () => import("@/pages/Logout.vue");
const MailReportAutomationView = () =>
  import("@/pages/MailReportAutomation.vue");
const MailReportSettingsView = () => import("@/pages/MailReportSettings.vue");
const OnboardingView = () => import("@/pages/OnboardingView.vue");
const PositionCreateManualView = () =>
  import("@/pages/PositionCreateManual.vue");
const PositionCreateVoiceView = () => import("@/pages/PositionCreateVoice.vue");
const PositionImportView = () => import("@/pages/PositionImport.vue");
const PositionInitialSetupView = () =>
  import("@/pages/PositionInitialSetup.vue");
const PositionIntegrationView = () => import("@/pages/PositionIntegration.vue");
const SimulationResultPage = () => import("@/pages/SimulationResultPage.vue");
const PositionListView = () => import("@/pages/PositionList.vue");
const PositionPreview = () => import("@/pages/PositionPreview.vue");
const ResumeAddView = () => import("@/pages/ResumeAdd.vue");
const ResumeEditView = () => import("@/pages/ResumeEdit.vue");
const ResumeShowView = () => import("@/pages/ResumeShow.vue");
const TeamManagementView = () => import("@/pages/TeamManagement.vue");
const HelpCenterView = () => import("@/pages/HelpCenter.vue");

export const positionInitialSetupRoute = {
  path: "/positions/:positionId(\\d+)/initial-setup",
  name: "PositionInitialSetupEdit",
  component: PositionInitialSetupView,
};

export const positionPreviewRoute = {
  path: "/positions/:positionId(\\d+)/preview",
  name: "PositionPreview",
  component: PositionPreview,
};

export const teamManagementRoute = {
  path: "/team",
  name: "TeamManagement",
  component: TeamManagementView,
};

// Help Center's sticky header is 3.5rem tall; add a 1rem gap so router-driven hash
// jumps land a heading below it, matching help-center.scss's `h2 { scroll-margin-top: 4.5rem }`.
const HELP_CENTER_HEADER_OFFSET = 72;

// 50ms keeps the added scroll delay imperceptible next to image decode time without busy-polling.
const DOC_IMAGE_SETTLE_POLL_MS = 50;
// 1200ms caps the wait so a stalled image never blocks hash scrolling for more than
// about a second. Scroll then proceeds with the layout as-is.
const DOC_IMAGE_SETTLE_TIMEOUT_MS = 1200;

// Waits for doc images to finish loading so hash-anchor scrolling measures a settled
// layout (router modules may touch document directly).
const waitForDocImagesToSettle = async (): Promise<void> => {
  const deadline = Date.now() + DOC_IMAGE_SETTLE_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const images = document.querySelectorAll<HTMLImageElement>(
      ".help-center-doc-content img",
    );
    if (Array.from(images).every((image) => image.complete)) {
      return;
    }
    await new Promise((resolve) =>
      setTimeout(resolve, DOC_IMAGE_SETTLE_POLL_MS),
    );
  }
};

const router = createRouter({
  async scrollBehavior(to) {
    if (to.name === "HelpCenterDoc" && to.hash) {
      await waitForDocImagesToSettle();
      return { el: to.hash, top: HELP_CENTER_HEADER_OFFSET };
    }
    return { top: 0 };
  },
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "Home",
      component: HomeView,
      meta: { public: true },
    },
    {
      path: "/metrics",
      name: "RoiMetrics",
      component: RoiMetricsView,
    },
    {
      path: "/login",
      name: "Login",
      component: LoginView,
      meta: { public: true },
    },
    {
      path: "/logout",
      name: "Logout",
      component: LogoutView,
      meta: { public: true },
    },
    {
      path: "/dashboard/:candidateId(\\d+)?",
      name: "Dashboard",
      component: DashboardView,
    },
    {
      // This is a workaround to allow the user to navigate to the dashboard from the header.
      path: "/dashboard/all",
      redirect: "/dashboard",
    },
    {
      path: "/onboarding",
      name: "Onboarding",
      component: OnboardingView,
    },
    {
      path: "/usages",
      name: "AIUsage",
      component: AIUsageView,
    },
    {
      path: "/action-items",
      name: "ActionItems",
      component: ActionItemsView,
    },
    {
      path: "/automation/faq-library",
      name: "FaqLibrary",
      component: FaqLibraryView,
    },
    {
      path: "/positions",
      name: "PositionList",
      component: PositionListView,
    },
    {
      path: "/automation",
      name: "Automation",
      component: AutomationView,
    },
    {
      path: "/automation/rejection-message",
      name: "AutoRejectionMessage",
      component: AutoRejectionMessageView,
    },
    {
      path: "/automation/interview-report-opening-text",
      name: "InterviewReportOpeningText",
      component: InterviewReportOpeningTextView,
    },
    {
      path: "/automation/interview-invite",
      name: "AutoInterviewInvite",
      component: AutoInterviewInviteView,
    },
    {
      path: "/automation/ats-pipeline-sync",
      name: "AtsPipelineSync",
      component: AtsPipelineSyncView,
    },
    {
      path: "/team/email-notifications",
      name: "MailReportAutomation",
      component: MailReportAutomationView,
    },
    {
      path: "/team/email-notifications/:positionId(\\d+)",
      name: "MailReportSettings",
      component: MailReportSettingsView,
    },
    {
      path: "/positions/import",
      name: "PositionImport",
      component: PositionImportView,
    },
    {
      path: "/positions/integration",
      name: "PositionIntegration",
      component: PositionIntegrationView,
    },
    positionInitialSetupRoute,
    {
      path: "/positions/create",
      redirect: { name: "PositionCreateManual" },
    },
    {
      path: "/positions/create/manual",
      name: "PositionCreateManual",
      component: PositionCreateManualView,
    },
    {
      path: "/positions/create/voice",
      name: "PositionCreateVoice",
      component: PositionCreateVoiceView,
    },
    {
      path: "/positions/:positionId(\\d+)/simulation-result",
      name: "SimulationResult",
      component: SimulationResultPage,
    },
    positionPreviewRoute,
    {
      path: "/positions/:positionId(\\d+)/scheduling",
      name: "SchedulingList",
      component: CandidateListView,
    },
    {
      path: "/positions/:positionId(\\d+)/in-process",
      name: "InProcessList",
      component: CandidateListView,
    },
    {
      path: "/positions/:positionId(\\d+)/offer",
      name: "OfferList",
      component: CandidateListView,
    },
    {
      path: "/positions/:positionId(\\d+)/hire",
      name: "HireList",
      component: CandidateListView,
    },
    {
      path: "/positions/:positionId(\\d+)/onboarding",
      name: "OnboardingList",
      component: CandidateListView,
    },
    {
      path: "/positions/:positionId(\\d+)/candidates",
      name: "CandidateList",
      component: CandidateListView,
    },
    {
      // CandidateListView reads the candidate id from this route once on mount and opens the conversation card over itself.
      path: "/positions/:positionId(\\d+)/candidates/:candidateId(\\d+)",
      name: "CandidateView",
      component: CandidateListView,
    },
    {
      path: "/positions/:positionId(\\d+)/candidates/create",
      name: "CandidateAdd",
      component: CandidateEditView,
    },
    {
      path: "/positions/:positionId(\\d+)/candidates/:candidateId(\\d+)/edit",
      name: "CandidateEdit",
      component: CandidateEditView,
    },
    {
      path: "/positions/:positionId(\\d+)/resumes",
      name: "ResumeList",
      component: CandidateListView,
    },
    {
      path: "/positions/:positionId(\\d+)/resumes/create",
      name: "ResumeAdd",
      component: ResumeAddView,
    },
    {
      path: "/positions/:positionId(\\d+)/resumes/:resumeId(\\d+)/edit",
      name: "ResumeEdit",
      component: ResumeEditView,
    },
    {
      path: "/positions/:positionId(\\d+)/resumes/:resumeId(\\d+)/show",
      name: "ResumeShow",
      component: ResumeShowView,
    },
    {
      path: "/positions/:positionId(\\d+)/candidates/:candidateId(\\d+)/evaluation/reject",
      name: "Reject",
      component: RejectView,
    },
    {
      path: "/positions/:positionId(\\d+)/candidates/:candidateId(\\d+)/evaluation",
      name: "Evaluation",
      component: EvaluationView,
    },
    {
      path: "/positions/:positionId(\\d+)/candidates/:candidateId(\\d+)/evaluation/transcript-record",
      name: "EvaluationTranscriptRecord",
      component: EvaluationTranscriptRecordView,
    },
    {
      path: "/integrations/add",
      name: "IntegrationAdd",
      component: IntegrationAddView,
    },
    teamManagementRoute,
    {
      path: "/candidates/:candidateId(\\d+)?",
      name: "GlobalCandidatesList",
      component: GlobalCandidatesListView,
    },
    {
      path: "/candidates/all",
      name: "AllCandidatesRedirect",
      component: AllCandidatesRedirectView,
    },
    {
      path: "/docs",
      redirect: { name: "HelpCenterDoc", params: { slug: "introduction" } },
    },
    {
      path: "/docs/:slug",
      name: "HelpCenterDoc",
      component: HelpCenterView,
    },
  ],
});

router.beforeEach(authenticationGuard);
installChunkRecovery(router);

// Routes where sidebar should be automatically collapsed (deeper-than-list screens)
const SIDEBAR_COLLAPSED_ROUTES = [
  // Per-position candidate lists (entered from Jobs)
  "CandidateList",
  "ResumeList",
  "SchedulingList",
  "InProcessList",
  "OfferList",
  "HireList",
  "OnboardingList",
  // Action Items: the same wide tables, reached from the nav rather than from Jobs
  "ActionItems",
  // Evaluation flow
  "Evaluation",
  "EvaluationTranscriptRecord",
  "Reject",
  // Candidate / Resume detail and edit
  "CandidateView",
  "CandidateEdit",
  "CandidateAdd",
  "ResumeShow",
  "ResumeEdit",
  "ResumeAdd",
  // AI Agent Setup (Position) edit
  "PositionInitialSetupEdit",
  // Simulation result review
  "SimulationResult",
];

router.afterEach((to, from) => {
  const toRequiresCollapse = SIDEBAR_COLLAPSED_ROUTES.includes(
    to.name as string,
  );
  const fromRequiresCollapse = SIDEBAR_COLLAPSED_ROUTES.includes(
    from.name as string,
  );

  if (toRequiresCollapse && !fromRequiresCollapse) {
    // Entering evaluation page: collapse sidebar with animation
    store.dispatch("sidebar/collapseSidebar");
  } else if (!toRequiresCollapse && fromRequiresCollapse) {
    // Leaving evaluation page: expand sidebar with animation
    store.dispatch("sidebar/expandSidebar");
  }
});

export default router;
